/**
 * Copyright (c) Mik BRY
 * mik@mikbry.com
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 */
import Interface from '../interface';
import Box from '../components/Box';
import Header from '../components/Header';
import List from '../components/List';
import Footer from '../components/Footer';
import Message from '../components/Message';
import Input from '../components/Input';
import Theme from '../Theme';

const StyledMessenger = Interface.styled(Box)`
  color: ${(props) => props.theme.palette.onSurface};
  font-family: ${(props) => props.theme.font.family};
  font-size: ${(props) => props.theme.font.size};
  font-style: ${(props) => props.theme.font.style};
  font-weight: ${(props) => props.theme.font.weight};
  line-height: 1.1;
  overflow-wrap: break-word;
  padding: 0px;
  width: ${(props) => props.theme.messenger.width};
  height: ${(props) => props.theme.messenger.height};
  display: flex;
  border-radius: ${(props) => `${props.theme.radius}px`};
  flex-direction: column;
  box-shadow: ${(props) => props.theme.shadow};
  
  transform: perspective( 2000px ) rotateY( 0deg );
  transition: all .3s linear;
  position: relative;
  backface-visibility: hidden;

  & > p {
    font-size: 13px;
    line-height: 14px;
    text-align: end;
    padding-right: 16px;
    margin: 4px 0;

  }

  &.isexpanded .ziiir-conversation {
    animation: 0.675s cubic-bezier(0.4, 0, 0.2, 1) 0.45s 1 normal both running enterConversation;
    animation-delay: 0.45s;
    animation-fill-mode: both;
  }

  & .ziiir-conversation {
    padding: 16px;
    width: calc(100% - 32px);
    height: calc(100% - 32px);
    overflow-x: hidden;
  }

  & .ziiir-conversation > div {
    webkit-flex: 0 0 100%;
  }

  @keyframes enterConversation {
    0% {
      height: 0;
      padding: 0;
      opacity: 0;
    }
  
    50% {
      opacity: 0;
    }
  
    100% {
      height: 100%;
      padding: 16px;
      opacity: 1;
    }
  }
`;

StyledMessenger.defaultProps = {
  theme: Theme,
};

const MessengerHeader = Interface.styled(Header)`
  border-bottom: ${(props) => `1px solid ${props.theme.palette.border}`};
  border-radius: ${(props) => `${props.theme.radius}px ${props.theme.radius}px 0 0`};
`;

MessengerHeader.defaultProps = {
  theme: Theme,
};

const MessengerFooter = Interface.styled(Footer)`
  border-top: ${(props) => `1px solid ${props.theme.palette.border}`};
  border-radius: ${(props) => `0 0 ${props.theme.radius}px ${props.theme.radius}px`};
  display: flex;
  flex-direction: column;

  & button:hover {
    color: ${(props) => props.theme.palette.onSurface};
  }
  & button img {
    background-color: transparent;
    filter: opacity(30%);
  }
  & button:hover img {
    background-color: transparent;
    filter: opacity(70%);
  }
`;

MessengerFooter.defaultProps = {
  theme: Theme,
};

const Conversation = Interface.styled(List)`
  transform-origin: center bottom 0px;
  scroll-behavior: smooth;
  
  & > .ziiir-message-container > .ziiir-message > .ziiir-quick-replies {
    display: none;
  }
  & > .ziiir-message-container:last-child > .ziiir-message > .ziiir-quick-replies {
    display: flex;
  }
  & > .ziiir-file-input > .ziiir-message-container > .ziiir-message > .ziiir-quick-replies {
    display: none;
  }
  & > .ziiir-file-input:last-child > .ziiir-message-container > .ziiir-message > .ziiir-quick-replies {
    display: flex;
  }
  & > .ziiir-message-container > .ziiir-message > .ziiir-quick-replies > .ziiir-quickreply:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  & > .ziiir-file-input > .ziiir-message-file-input {
    display: none;
  }
  & > .ziiir-file-input:last-child > .ziiir-message-file-input {
    display: flex;
  }
  & > .ziiir-carousel > .ziiir-message-carousel {
    display: none;
  }
  & > .ziiir-carousel:last-child > .ziiir-message-carousel {
    display: flex;
  }
`;

const FooterInputContainer = Interface.styled('div')`
  display: flex;
  width: 100%;
  padding: 4px;
  margin: 0;
  border: none;
  outline: none;
`;

const FooterInput = Interface.styled(Input)`
  width: 100%;
  padding: 4px;
  margin: 0;
  border: none;
  outline: none;
`;

const SendButton = Interface.styled('button')`
  display: ${(props) => (props.sendButton ? 'block' : 'none')};
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 16px;
  padding-right: 0;
  color: ${(props) => props.theme.palette.disabledText};
  background-color: transparent;
`;
SendButton.defaultProps = {
  theme: Theme,
};

const e = Interface.createElement;
const m = (createdtime, msg, fromUser, avatar, onAction, hideDate, hasPrevious, hasNext, quickReplies, template, showTemplateActions) =>
  e(
    Message,
    {
      key: createdtime,
      createdtime,
      avatar,
      fromUser,
      onAction,
      hideDate,
      hasPrevious,
      hasNext,
      quickReplies,
      template,
      showTemplateActions,
    },
    msg,
  );
const Messenger = ({
  isExpanded = true,
  input = { display: true },
  header: _header = {},
  messages = [],
  onMessage,
  onClick,
  onAction,
  hideDate = false,
}) => {
  const { text: headerText = '', ...header } = _header;
  const inputRef = Interface.useRef(null);
  const Messages = e(
    Conversation,
    { isExpanded, className: 'ziiir-conversation' },
    messages
      .sort((m1, m2) => m1.created_time > m2.created_time)
      .filter((msg) => msg.text !== '')
      .map((msg, i, ms) => {
        const prev = ms[i - 1];
        const hasPrevious = !!prev && prev.from === msg.from && msg.created_time - prev.created_time < 2000;
        const prevText = typeof prev?.text === 'object' && Object.hasOwn(prev.text, 'title') ? prev.text.title : prev?.text;
        const hasEmptyPrevious = !(prevText?.replace(/{@(([a-zA-Z0-9_-]+)(=([a-zA-Z0-9 _-]+))?)@}/g, '')?.trim());
        const next = ms[i + 1];
        const hasNext = !!next && next.from === msg.from && next.created_time - msg.created_time < 2000;
        return m(
          msg.created_time,
          msg.text,
          msg.from === 'user',
          msg.avatar,
          onAction,
          hideDate,
          hasPrevious && !hasEmptyPrevious,
          hasNext,
          msg.quick_replies,
          msg.template,
          !(msg.template && ['carousel', 'file'].includes(msg.template.type) && !!next),
        );
      }),
  );
  let inputComponent = 'ziiir.com';
  const handleKey = (event) => {
    const value = event.target.value || '';
    if (event.key === 'Enter' && value.length > 0) {
      if (onMessage) {
        onMessage(value);
      }
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    }
  };
  const handleSend = () => {
    const { value } = inputRef.current;
    if (value && value.length && onMessage) {
      onMessage(value);
      inputRef.current.value = '';
    }
  };

  if (input.display) {
    inputComponent = e(FooterInput, {
      className: 'ziiir-input',
      onKeyUp: handleKey,
      ref: inputRef,
      placeholder: input.placeholder || 'Your message',
    });
    let buttoncontent = 'send';
    if (input.sendButton && input.sendButton.icon) {
      buttoncontent = e('img', {
        src: input.sendButton.icon.src
          ? input.sendButton.icon.src
          : 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMi4wMSAyMUwyMyAxMiAyLjAxIDMgMiAxMGwxNSAyLTE1IDJ6Ii8+PC9zdmc+',
      });
    }
    inputComponent = e(
      FooterInputContainer,
      {
        className: 'ziiir-input-container',
      },
      inputComponent,
      e(
        SendButton,
        {
          onClick: handleSend,
          className: 'ziiir-send-button',
          sendButton: input.sendButton && !input.sendButton.disableDesktop,
        },
        buttoncontent,
      ),
    );
  }
  return e(
    StyledMessenger,
    { className: `ziiir-messenger-inner${isExpanded ? ' isexpanded' : ''}`, onClick },
    e(MessengerHeader, header, headerText),
    Messages,
    // e('p', null, 'Powered by ', e('a', { href: 'https://ziiir.com', target: '_blank' }, 'ziiir.com')),
    e(MessengerFooter, {
        id: 'ziiir-messenger-footer',
    }, inputComponent),
  );
};

export default Messenger;
